<template>
  <dl
    v-if="facts.length"
    class="fu-drf"
    :class="{ 'fu-drf--phone': layout === 'phone', 'fu-drf--tablet': layout === 'tablet' }"
    :style="{ '--fu-drf-cols': cols }"
    data-test="fu-drawer-facts"
  >
    <div v-for="f in facts" :key="f.key" class="fu-drf__cell" :data-test="`fu-drawer-fact-${f.key}`">
      <dt class="fu-drf__label">{{ f.label }}</dt>
      <dd class="fu-drf__value" :title="f.value">{{ f.value }}</dd>
      <dd v-if="f.sub" class="fu-drf__sub" :class="{ 'fu-drf__sub--danger': f.tone === 'danger' }" :title="f.sub">{{ f.sub }}</dd>
    </div>
  </dl>
</template>

<script>
/**
 * DrawerFacts — the design's fact grid under the header: 4 columns on a wide drawer, 2 on a phone.
 * `facts` = drawerFacts.buildDrawerFacts() → [{ key, label, value, sub, tone }]; only fields today's
 * drawer loads (FACT_GAPS lists the design cells with no data source). `layout` from DrawerLayout.
 * Presentational: no store, no mixin.
 */
export default {
  name: 'DrawerFacts',
  props: {
    facts: { type: Array, default: () => [] },
    layout: { type: String, default: 'desktop' },
  },
  computed: {
    cols() {
      return String(this.layout === 'phone' ? 2 : Math.max(1, Math.min(4, this.facts.length)));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drf {
  display: grid;
  grid-template-columns: repeat(var(--fu-drf-cols, 4), minmax(0, 1fr));
  gap: 1px;
  margin: 16px 0 0;
  background: $fu-border;
  border: 1px solid $fu-border;
  border-radius: 12px;
  overflow: hidden;
}

.fu-drf__cell {
  min-width: 0;
  padding: 10px 14px;
  background: $fu-surface;
}

.fu-drf__label {
  @include fu-ellipsis;
  margin: 0;
  font-size: 11px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $fu-text-subtle;
}

.fu-drf__value {
  @include fu-ellipsis;
  @include fu-tabular;
  margin: 3px 0 0;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.fu-drf__sub {
  @include fu-ellipsis;
  margin: 1px 0 0;
  font-size: 12px;
  color: $fu-text-muted;
}

.fu-drf__sub--danger {
  color: $fu-danger-text;
  font-weight: $fu-fw-semibold;
}

// Tablet: a tighter grid so the stacked form gets the room (header + facts were ~245px at 820).
.fu-drf--tablet { margin-top: 10px; border-radius: 10px; }
.fu-drf--tablet .fu-drf__cell { padding: 6px 12px; }
.fu-drf--tablet .fu-drf__label { font-size: 10.5px; }
.fu-drf--tablet .fu-drf__value { margin-top: 1px; font-size: 13.5px; }
.fu-drf--tablet .fu-drf__sub { margin-top: 0; font-size: 11.5px; }

.fu-drf--phone .fu-drf__cell { padding: 9px 12px; }
</style>
